import { Injectable } from '@angular/core';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { environment } from '../../environments/environment';

declare global {
	interface Window {
		Pusher: any;
		Echo: any;
	}
}

@Injectable({
	providedIn: 'root'
})
export class EchoService {
	private echo: Echo<any> | null = null;
	private connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error' = 'disconnected';
	private reconnectAttempts = 0;
	private maxReconnectAttempts = 5;

	constructor() {
		this.initializeEcho();
	}

	private initializeEcho(): void {
		try {
			this.connectionStatus = 'connecting';

			// Make Pusher available globally
			window.Pusher = Pusher;

			// Determine if we should use TLS based on environment
			const isLocalhost = environment.reverbHost === 'localhost' || environment.reverbHost === '127.0.0.1';
			const shouldUseTLS = !isLocalhost;

			const config: any = {
				forceTLS: shouldUseTLS,
				authEndpoint: `${environment.apiUrl}broadcasting/auth`,
				auth: {
					headers: {
						'Accept': 'application/json',
						'X-Requested-With': 'XMLHttpRequest'
					}
				},
				// Use custom authorizer for session-based auth with Sanctum
				authorizer: (channel: any) => {
					return {
						authorize: (socketId: string, callback: (error: any, data: any) => void) => {
							fetch(`${environment.apiUrl}broadcasting/auth`, {
								method: 'POST',
								credentials: 'include', // Send session cookies
								headers: {
									'Content-Type': 'application/json',
									'Accept': 'application/json',
									'X-Requested-With': 'XMLHttpRequest'
								},
								body: JSON.stringify({
									socket_id: socketId,
									channel_name: channel.name
								})
							})
								.then((response) => {
									if (!response.ok) {
										throw new Error(`Auth failed with status ${response.status}`);
									}
									return response.json();
								})
								.then((data) => callback(null, data))
								.catch((error) => callback(error, null));
						}
					};
				}
			};

			// Configure based on broadcasting driver
			if (environment.broadcastDriver === 'reverb') {
				config.broadcaster = 'reverb';
				config.key = environment.reverbKey;
				config.wsHost = environment.reverbHost;
				config.wsPort = environment.reverbPort;
				config.wssPort = environment.reverbPort;
				config.enabledTransports = ['ws', 'wss'];
			} else {
				config.broadcaster = 'pusher';
				config.key = environment.pusherKey;
				config.cluster = environment.pusherCluster;
			}

			this.echo = new Echo<any>(config);

			// Assign to global window for debugging
			window.Echo = this.echo;

			this.setupConnectionHandlers();
			this.connectionStatus = 'connected';
			this.reconnectAttempts = 0;
		} catch (error) {
			console.error('Failed to initialize Laravel Echo:', error);
			this.connectionStatus = 'error';
			this.handleReconnection();
		}
	}

	private setupConnectionHandlers(): void {
		if (!this.echo) return;

		// Setup connection event handlers for monitoring
		const connector = (this.echo as any).connector;

		if (connector && connector.pusher) {
			connector.pusher.connection.bind('connected', () => {
				this.connectionStatus = 'connected';
				this.reconnectAttempts = 0;
			});

			connector.pusher.connection.bind('disconnected', () => {
				this.connectionStatus = 'disconnected';
			});

			connector.pusher.connection.bind('error', (error: any) => {
				this.connectionStatus = 'error';
				console.error('Echo connection error:', error);
				this.handleReconnection();
			});
		}
	}

	private handleReconnection(): void {
		if (this.reconnectAttempts < this.maxReconnectAttempts) {
			this.reconnectAttempts++;
			const delay = Math.pow(2, this.reconnectAttempts) * 1000; // Exponential backoff

			setTimeout(() => {
				this.reconnect();
			}, delay);
		} else {
			console.error('Max reconnection attempts reached');
		}
	}

	public getEcho(): Echo<any> | null {
		return this.echo;
	}

	public channel(channelName: string) {
		if (!this.echo) {
			console.warn('Echo not initialized');
			return null;
		}
		return this.echo.channel(channelName);
	}

	public privateChannel(channelName: string) {
		if (!this.echo) {
			console.warn('Echo not initialized');
			return null;
		}
		return this.echo.private(channelName);
	}

	public presenceChannel(channelName: string) {
		if (!this.echo) {
			console.warn('Echo not initialized');
			return null;
		}
		return this.echo.join(channelName);
	}

	public leave(channelName: string): void {
		if (!this.echo) {
			console.warn('Echo not initialized');
			return;
		}
		this.echo.leave(channelName);
	}

	public disconnect(): void {
		if (this.echo) {
			this.echo.disconnect();
		}
	}

	public reconnect(): void {
		this.disconnect();
		this.initializeEcho();
	}

	public getConnectionStatus(): string {
		return this.connectionStatus;
	}

	public isConnected(): boolean {
		return this.connectionStatus === 'connected';
	}
}
